/**
 * What a person is shown before accepting a suggested (or stale) pair.
 *
 * Found 2026-09-29 on a real client month: the card printed the first twelve
 * characters of two content hashes — `books 2db73cd884cd ↔ 2B d7dc5901df98` —
 * so the one person asked to accept could not say which invoices the pair
 * joined. It was one invoice numbered `…/2026/NNNN` in the register and
 * `…/2026-27/NNNN` in the 2B. This says so: both sides in full, and which
 * fields differ, because the difference is the whole reason a person is asked.
 */

import type { MatchSide, ReconMatchOut } from './api'

const HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const

/** Indian digit grouping, paise in — the same reading as the rest of the screen. */
function rupees(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * One calendar day, however the side wrote it. On the same real pair the books
 * row read `2026-07-28 00:00:00` and the 2B row `28-07-2026` — the same day, and
 * a string compare would have called every pair's date a difference.
 */
export function dayOf(value: string | null): string | null {
  if (!value) return null
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value.trim())
  if (dmy) return `${dmy[1]!.padStart(2, '0')}-${dmy[2]!.padStart(2, '0')}-${dmy[3]}`
  return value
}

function tax(side: MatchSide): number {
  return HEADS.reduce((sum, head) => sum + (side[head] ?? 0), 0)
}

/** The heads a side actually carries — `IGST`, or `CGST + SGST`. */
function headsOf(side: MatchSide): string[] {
  return HEADS.filter((head) => (side[head] ?? 0) !== 0).map((head) => head.toUpperCase())
}

/**
 * Tax by head, never a total alone. Found on the first real pair the fixed card
 * drew: IGST in the 2B against CGST + SGST in the books, the same total — so a
 * total-only line showed two identical figures above "Differs in: tax". Which heads is the inter- versus intra-state
 * question, and it is the one a reviewer has to answer before accepting.
 */
function taxText(side: MatchSide): string {
  const heads = HEADS.filter((head) => (side[head] ?? 0) !== 0)
  if (heads.length === 0) return 'no tax'
  return heads.map((head) => `${head.toUpperCase()} ₹${rupees(side[head])}`).join(' + ')
}

function line(side: MatchSide): string {
  return (
    `${side.doc_no ?? 'no invoice number'} · ${dayOf(side.doc_date) ?? 'no date'} · ` +
    `₹${rupees(side.taxable)} taxable · ${taxText(side)}`
  )
}

/** One row on its own — the supplier, then the line a match card prints for it. */
export function describeSide(side: MatchSide): string {
  return [side.supplier_gstin, line(side)].filter(Boolean).join(' · ')
}

export interface PairView {
  /** Said once when both sides agree — a supplier is not a difference. */
  supplier: string | null
  books: string
  tb: string
  /** Plain words, in the order a reviewer checks them. Empty = only the rung differs. */
  differs: string[]
}

export function describePair(match: ReconMatchOut): PairView {
  const { books, tb } = match
  const view: PairView = {
    supplier: null,
    books: books ? line(books) : 'This books row is no longer on the books.',
    tb: tb ? line(tb) : 'This 2B row is not in the current import.',
    differs: [],
  }
  if (!books || !tb) {
    view.supplier = books?.supplier_gstin ?? tb?.supplier_gstin ?? null
    return view
  }
  if (books.supplier_gstin === tb.supplier_gstin) {
    view.supplier = books.supplier_gstin
  } else {
    view.differs.push('supplier GSTIN')
  }
  if (books.doc_no !== tb.doc_no) view.differs.push('invoice number')
  if (dayOf(books.doc_date) !== dayOf(tb.doc_date)) view.differs.push('date')
  if (books.taxable !== tb.taxable) view.differs.push('taxable value')
  if (tax(books) !== tax(tb)) {
    view.differs.push('tax')
  } else if (HEADS.some((head) => (books[head] ?? 0) !== (tb[head] ?? 0))) {
    const split = (side: MatchSide) => headsOf(side).join(' + ') || 'none'
    view.differs.push(`tax split (${split(books)} vs ${split(tb)})`)
  }
  return view
}
